import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { errMsg, useApi } from "@/lib/api";
import { eur } from "@/lib/format";
import DateField from "@/components/DateField";
import { uploadPhotos } from "@/components/PhotosDialog";
import { Camera } from "lucide-react";

const toNum = (v) => {
  let s = String(v).trim().replace(/\s/g, "");
  if (s.includes(",")) s = s.replace(/\./g, "").replace(",", ".");
  else if (/^\d{1,3}(\.\d{3})+$/.test(s)) s = s.replace(/\./g, "");
  return parseFloat(s) || 0;
};
const fmt = (v) => (v == null ? "" : String(Math.round(v * 1000) / 1000).replace(".", ","));
const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const suggestPrice = (yards, grades, yardId, gradeKey) =>
  yards.find((y) => y.id === yardId)?.prices?.[gradeKey] ?? grades.find((g) => g.key === gradeKey)?.price_eur_kg;

export default function DeliveryFormDialog({ delivery, yards, grades, onClose, onSaved }) {
  const api = useApi();
  const init = delivery || { yard_id: yards[0]?.id, grade_key: grades[0]?.key, date: today(), kg: "", note: "" };
  const [f, setF] = useState({ ...init, kg: fmt(init.kg), price: fmt(delivery ? delivery.price_eur_kg : suggestPrice(yards, grades, init.yard_id, init.grade_key)) });
  const [saving, setSaving] = useState(false);
  const [files, setFiles] = useState([]);

  const pick = (k, v) => {
    const next = { ...f, [k]: v };
    next.price = fmt(suggestPrice(yards, grades, next.yard_id, next.grade_key));
    setF(next);
  };

  const save = async () => {
    if (toNum(f.kg) <= 0) return toast.error("Bitte Gewicht angeben");
    setSaving(true);
    const body = { yard_id: f.yard_id, grade_key: f.grade_key, date: f.date, kg: toNum(f.kg), price_eur_kg: toNum(f.price), note: f.note || "" };
    try {
      const r = delivery ? await api.put(`/deliveries/${delivery.id}`, body) : await api.post("/deliveries", body);
      if (files.length) await uploadPhotos(api, r.data.id, files);
      toast.success("Anlieferung gespeichert");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg border-slate-200 bg-white" data-testid="dialog-delivery-form">
        <DialogHeader><DialogTitle>{delivery ? "Anlieferung bearbeiten" : "Neue Anlieferung"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label>Wertstoffhof</Label>
            <Select value={f.yard_id} onValueChange={(v) => pick("yard_id", v)}>
              <SelectTrigger data-testid="select-delivery-yard"><SelectValue placeholder="Wertstoffhof wählen" /></SelectTrigger>
              <SelectContent>{yards.map((y) => <SelectItem key={y.id} value={y.id}>{y.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label>Sorte</Label>
            <Select value={f.grade_key} onValueChange={(v) => pick("grade_key", v)}>
              <SelectTrigger data-testid="select-delivery-grade"><SelectValue /></SelectTrigger>
              <SelectContent>{grades.map((g) => <SelectItem key={g.key} value={g.key}>{g.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Datum</Label><DateField value={f.date} onChange={(v) => setF({ ...f, date: v })} testId="input-delivery-date" /></div>
          <div><Label>Gewicht (kg)</Label><Input inputMode="decimal" value={f.kg} onChange={(e) => setF({ ...f, kg: e.target.value })} placeholder="z.B. 250" data-testid="input-delivery-kg" /></div>
          <div className="col-span-2">
            <Label>Erzielter Preis (€/kg)</Label>
            <Input inputMode="decimal" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} data-testid="input-delivery-price" />
            <p className="mt-1 text-xs text-slate-500">Vorbelegt mit dem Hof-Preis bzw. Richtpreis – bitte den tatsächlich gezahlten Preis eintragen.</p>
          </div>
          <div className="col-span-2"><Label>Notiz (optional)</Label><Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="z.B. Wiegeschein-Nr." data-testid="input-delivery-note" /></div>
          <div className="col-span-2">
            <Label>Wiegeschein-Foto (optional)</Label>
            <label className="mt-1 flex cursor-pointer items-center gap-3 rounded-md border border-dashed border-slate-300 px-3 py-2.5 text-sm text-slate-600 transition-colors hover:border-orange-400 hover:bg-orange-50/50" data-testid="label-delivery-photo">
              <Camera className="h-4 w-4 text-orange-600" />
              <span className="truncate">{files.length ? files.map((x) => x.name).join(", ") : "Foto aufnehmen oder auswählen"}</span>
              <input type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={(e) => setFiles([...e.target.files])} data-testid="input-delivery-photo" />
            </label>
          </div>
        </div>
        <div className="flex items-center justify-between rounded-xl bg-orange-50 px-4 py-3">
          <span className="text-sm text-slate-600">Erlös</span>
          <span className="num text-2xl font-bold text-orange-700" data-testid="text-delivery-revenue">{eur(toNum(f.kg) * toNum(f.price))}</span>
        </div>
        <DialogFooter><Button onClick={save} disabled={saving || !f.yard_id} data-testid="btn-save-delivery">Speichern</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
